/**
 * EV strikes — overstay strikes against units that left their car plugged
 * into a society EV charger past the policy window.
 *
 * Data flow:
 *   - GET /v1/parking/ev-strikes?unit_id=&from=&to=
 *   - POST /v1/parking/ev-strikes/:id/clear
 *
 * Two views (tabs):
 *   1. List — table of recent strikes filtered by unit + date range.
 *   2. By unit — aggregated counts in last 30 days; ≥3 strikes highlighted
 *      red with an Escalate CTA the admin can wire to a notice/penalty bill.
 *
 * Realtime: subscribes to `parking:ev_strike_escalation` and pops a toast so
 * the admin can react before the resident calls.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ZapOff, AlertTriangle, Filter, ShieldAlert, Users } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { TabsPills } from '@/components/ui/tabs-pills';
import { Textarea } from '@/components/ui/textarea';
import { useParkingRealtime } from '@/hooks/useParkingRealtime';
import { formatDateTime } from '@/lib/format';

type StrikeStatus = 'active' | 'cleared';

interface EvStrike {
  id: string;
  society_id?: string;
  unit_id: string;
  charger_id?: string | null;
  ev_session_id?: string | null;
  reason: string;
  minutes_overstayed: number;
  occurred_at: string;
  status: StrikeStatus;
  cleared_at?: string | null;
  cleared_notes?: string | null;
  unit?: { id: string; number: string; block?: { id: string; name: string } | null } | null;
  charger?: { id: string; location_label?: string | null } | null;
  ev_session?: { id: string; started_at: string; ended_at?: string | null } | null;
}

interface ByUnitRow {
  unit_id: string;
  unit_number: string;
  block_name?: string | null;
  strike_count: number;
  last_strike_at?: string | null;
}

const STATUS_LABEL: Record<StrikeStatus, string> = {
  active: 'Active',
  cleared: 'Cleared'
};

const STATUS_BADGE: Record<StrikeStatus, 'alert' | 'muted'> = {
  active: 'alert',
  cleared: 'muted'
};

const ESCALATION_THRESHOLD = 3;

type Tab = 'list' | 'by-unit';

export function ParkingEvStrikesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('list');
  const [unitFilter, setUnitFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useParkingRealtime({
    onEvStrikeEscalation: (e) => {
      toast.warning('EV strike escalation', {
        description: `Unit hit ${e.strike_count} strikes — review escalation queue.`
      });
      void queryClient.invalidateQueries({ queryKey: ['parking', 'ev-strikes'] });
    }
  });

  const listQuery = useQuery({
    queryKey: ['parking', 'ev-strikes', 'list', { unitFilter, from, to }],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (unitFilter) qs.set('unit_id', unitFilter);
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      return get<{ items: EvStrike[] }>(`/parking/ev-strikes?${qs.toString()}`);
    }
  });

  const items = listQuery.data?.items ?? [];

  // Derive by-unit aggregate client-side from the same response. The server
  // may eventually offer a dedicated endpoint; for now this keeps the page
  // useful without extra round-trips.
  const byUnit: ByUnitRow[] = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const buckets = new Map<string, ByUnitRow>();
    for (const s of items) {
      if (s.status !== 'active') continue;
      const occurred = new Date(s.occurred_at).getTime();
      if (Number.isNaN(occurred) || occurred < cutoff) continue;
      const existing = buckets.get(s.unit_id);
      if (existing) {
        existing.strike_count += 1;
        if (!existing.last_strike_at || s.occurred_at > existing.last_strike_at) {
          existing.last_strike_at = s.occurred_at;
        }
      } else {
        buckets.set(s.unit_id, {
          unit_id: s.unit_id,
          unit_number: s.unit?.number ?? '—',
          block_name: s.unit?.block?.name ?? null,
          strike_count: 1,
          last_strike_at: s.occurred_at
        });
      }
    }
    return Array.from(buckets.values()).sort((a, b) => b.strike_count - a.strike_count);
  }, [items]);

  const [clearTarget, setClearTarget] = useState<EvStrike | null>(null);
  const [clearNotes, setClearNotes] = useState('');

  const clearMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      post<EvStrike>(`/parking/ev-strikes/${id}/clear`, { notes }),
    onSuccess: () => {
      toast.success('Strike cleared');
      setClearTarget(null);
      setClearNotes('');
      void queryClient.invalidateQueries({ queryKey: ['parking', 'ev-strikes'] });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  // When list filters change, re-fetch by-unit derivation too (handled
  // automatically because both reads share the same query). The effect only
  // exists to surface that intent.
  useEffect(() => {
    void listQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitFilter, from, to]);

  return (
    <PageHeader
      title="EV charging strikes"
      description="Overstay strikes against units that occupied an EV charger past policy."
    >
      <div className="mb-vt-4 flex items-center justify-between gap-vt-3">
        <TabsPills
          aria-label="EV strike views"
          value={tab}
          onChange={(v) => setTab(v as Tab)}
          options={[
            { value: 'list', label: 'All strikes' },
            { value: 'by-unit', label: 'Strikes by unit', count: byUnit.length }
          ]}
        />
      </div>

      {/* Filters */}
      <Card className="mb-vt-4">
        <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
          <div className="flex w-full items-center gap-vt-2 text-vt-13 font-medium text-vt-text-muted sm:w-auto">
            <Filter className="h-4 w-4" />
            Filters
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="strk-unit">Unit ID</Label>
            <Input
              id="strk-unit"
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
              placeholder="UUID"
              className="w-56"
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="strk-from">From</Label>
            <Input
              id="strk-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="strk-to">To</Label>
            <Input
              id="strk-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-44"
            />
          </div>
          {(unitFilter || from || to) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setUnitFilter('');
                setFrom('');
                setTo('');
              }}
            >
              Reset
            </Button>
          )}
        </CardContent>
      </Card>

      {/* List view */}
      {tab === 'list' &&
        (listQuery.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : listQuery.isError ? (
          <Card>
            <CardContent className="text-vt-alert">{getErrorMessage(listQuery.error)}</CardContent>
          </Card>
        ) : items.length === 0 ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<ZapOff className="h-7 w-7" />}
                title="No EV strikes"
                description="Strikes will appear here when units exceed the configured charger occupancy window."
              />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unit</TableHead>
                    <TableHead>Charger</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Overstay</TableHead>
                    <TableHead>Occurred</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">
                        {s.unit?.block?.name ? `${s.unit.block.name}-` : ''}
                        {s.unit?.number ?? '—'}
                      </TableCell>
                      <TableCell>{s.charger?.location_label ?? '—'}</TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">{s.reason}</TableCell>
                      <TableCell className="text-right font-mono">
                        {s.minutes_overstayed} min
                      </TableCell>
                      <TableCell className="text-vt-13">{formatDateTime(s.occurred_at)}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {s.status === 'active' ? (
                          <Button variant="ghost" size="sm" onClick={() => setClearTarget(s)}>
                            Clear
                          </Button>
                        ) : (
                          <span className="text-vt-12 text-vt-text-muted">
                            {s.cleared_at ? formatDateTime(s.cleared_at) : '—'}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))}

      {/* By unit view */}
      {tab === 'by-unit' &&
        (listQuery.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : byUnit.length === 0 ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<Users className="h-7 w-7" />}
                title="No active strikes in last 30 days"
                description="Once strikes accumulate, units at risk of escalation will surface here."
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2 xl:grid-cols-3">
            {byUnit.map((u) => {
              const escalate = u.strike_count >= ESCALATION_THRESHOLD;
              return (
                <Card
                  key={u.unit_id}
                  className={escalate ? 'border-vt-alert/50 bg-vt-alert-100/40' : undefined}
                >
                  <CardContent className="space-y-vt-3 p-vt-4">
                    <div className="flex items-start justify-between gap-vt-2">
                      <div>
                        <div className="text-vt-12 text-vt-text-muted">Unit</div>
                        <div className="text-vt-18 font-semibold text-vt-text">
                          {u.block_name ? `${u.block_name}-` : ''}
                          {u.unit_number}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-vt-12 text-vt-text-muted">Active strikes</div>
                        <div
                          className={`text-vt-22 font-semibold ${
                            escalate ? 'text-vt-alert' : 'text-vt-text'
                          }`}
                        >
                          {u.strike_count}
                        </div>
                      </div>
                    </div>
                    {u.last_strike_at ? (
                      <p className="text-vt-12 text-vt-text-muted">
                        Latest {formatDateTime(u.last_strike_at)}
                      </p>
                    ) : null}
                    {escalate ? (
                      <div className="flex items-center justify-between gap-vt-2 rounded-vt-sm bg-vt-alert-100 px-vt-3 py-vt-2 text-vt-13 text-vt-alert ring-1 ring-inset ring-vt-alert/15">
                        <span className="inline-flex items-center gap-1">
                          <ShieldAlert className="h-4 w-4" />
                          ≥{ESCALATION_THRESHOLD} strikes
                        </span>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() =>
                            toast.info(
                              'Escalation workflow opens a draft notice — wire to /notices in next iteration.'
                            )
                          }
                        >
                          Escalate
                        </Button>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))}

      {/* Clear strike dialog */}
      <Dialog
        open={Boolean(clearTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setClearTarget(null);
            setClearNotes('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-vt-2">
              <AlertTriangle className="h-5 w-5 text-vt-warn" />
              Clear strike
            </DialogTitle>
            <DialogDescription>
              {clearTarget
                ? `${clearTarget.minutes_overstayed} min overstay on ${formatDateTime(
                    clearTarget.occurred_at
                  )}.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-vt-1 py-vt-2">
            <Label htmlFor="strk-clear-notes">Notes</Label>
            <Textarea
              id="strk-clear-notes"
              rows={4}
              value={clearNotes}
              onChange={(e) => setClearNotes(e.target.value)}
              placeholder="Reason for clearing (audit trail)"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setClearTarget(null)} disabled={clearMutation.isPending}>
              Cancel
            </Button>
            <Button
              loading={clearMutation.isPending}
              onClick={() => {
                if (!clearTarget) return;
                clearMutation.mutate({ id: clearTarget.id, notes: clearNotes });
              }}
            >
              Clear strike
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageHeader>
  );
}
